import React, { useCallback, useEffect, useState } from "react";
import { X, Sparkles, SlidersHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchFaceShape,
  fetchFaceShapeCatalog,
  deleteFaceShape,
} from "../lib/api";
import { track } from "../lib/analytics";
import FaceShapeCalibrator from "./FaceShapeCalibrator";

/**
 * FaceShapeChip — affirming callout on the Routine page that
 * surfaces the face shape detected during the last AR mirror session,
 * with a "Calibrate" affordance the user can tap to confirm or
 * override the detection.
 */
const FaceShapeChip = () => {
  const [shape, setShape] = useState(null);
  const [source, setSource] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [content, setContent] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [forgotten, setForgotten] = useState(false);
  const [calibratorOpen, setCalibratorOpen] = useState(false);

  const applyShape = useCallback(
    (nextShape, nextSource, nextCatalog = catalog) => {
      if (!nextShape) {
        setShape(null);
        setContent(null);
        setSource(null);
        return;
      }
      setShape(nextShape);
      setSource(nextSource || null);
      const match = (nextCatalog || []).find((s) => s.id === nextShape);
      setContent(match || null);
    },
    [catalog],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profile, cat] = await Promise.all([
          fetchFaceShape().catch(() => ({ shape: null })),
          fetchFaceShapeCatalog().catch(() => ({ shapes: [] })),
        ]);
        if (cancelled) return;
        const nextCatalog = cat?.shapes || [];
        setCatalog(nextCatalog);
        if (profile?.shape) {
          applyShape(profile.shape, profile.source, nextCatalog);
        }
      } catch {
        /* silent — chip just won't render */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onForget = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await deleteFaceShape();
      track("face_shape_forgotten", { shape });
      setForgotten(true);
    } catch {
      setForgotten(true);
    }
  };

  if (!shape || !content || forgotten) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="relative bg-gradient-to-br from-[#FDF6F3] via-[#F9ECEA] to-[#F2E8E9] border border-[#E4D2DC] rounded-2xl p-5 pr-10 mb-6 overflow-hidden"
          data-testid="face-shape-chip"
        >
          <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-[#D5A8A4]/20 blur-2xl pointer-events-none" />

          <div className="flex items-start gap-3 relative z-10">
            <div className="shrink-0 h-10 w-10 rounded-full bg-white/80 border border-[#E4D2DC] flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#9B6A6C]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#9B6A6C]">
                  Your face shape
                </span>
                {source === "user_set" && (
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] text-[#5D4050] bg-white/70 border border-[#E4D2DC] rounded-full px-2 py-0.5"
                    data-testid="face-shape-chip-source-user"
                  >
                    Calibrated
                  </span>
                )}
              </div>
              <h4 className="text-[#2C2123] font-medium leading-snug mb-1">
                Your face reads as{" "}
                <span
                  className="italic font-serif text-[#5D4050]"
                  data-testid="face-shape-chip-label"
                >
                  {content.label}
                </span>
                {" "}— {content.headline}.
              </h4>
              <p
                className="text-sm text-[#5C4B51] leading-relaxed mb-3"
                data-testid="face-shape-chip-tip"
              >
                {content.tip}
              </p>
              <button
                type="button"
                onClick={() => setCalibratorOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9B6A6C] hover:text-[#5D4050] transition-colors underline-offset-4 hover:underline"
                data-testid="face-shape-chip-calibrate"
              >
                <SlidersHorizontal className="h-3 w-3" />
                {source === "user_set"
                  ? "Change my shape"
                  : "Not quite right? Calibrate"}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={onForget}
            aria-label="Forget my saved face shape"
            title="Forget this"
            className="absolute top-3 right-3 h-7 w-7 rounded-full text-[#9B6A6C] hover:bg-white/70 hover:text-[#5D4050] transition-colors flex items-center justify-center z-20"
            data-testid="face-shape-chip-forget"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      </AnimatePresence>

      <FaceShapeCalibrator
        open={calibratorOpen}
        currentShape={shape}
        onClose={() => setCalibratorOpen(false)}
        onSaved={(res) => {
          if (res?.shape) applyShape(res.shape, res.source);
        }}
        theme="light"
      />
    </>
  );
};

export default FaceShapeChip;
